import ParagraphView from './ParagraphView';
import ImagePickerPlugin from './ImagePickerPlugin';
import LinkEditorPlugin from './LinkEditorPlugin';
import { EditorView } from 'prosemirror-view';
import { dropCursor } from 'prosemirror-dropcursor';
import { gapCursor } from 'prosemirror-gapcursor';
import {
  undoInputRule,
  inputRules,
  wrappingInputRule,
  textblockTypeInputRule,
  smartQuotes,
  emDash,
  ellipsis
} from 'prosemirror-inputrules';
import { keymap } from 'prosemirror-keymap';
import {
  menuBar,
  wrapItem,
  Dropdown,
  joinUpItem,
  liftItem,
  undoItem,
  redoItem,
  icons,
  MenuItem
} from 'prosemirror-menu';

import { EditorState, Selection } from 'prosemirror-state';
import { wrapInList, splitListItem, liftListItem, sinkListItem } from 'prosemirror-schema-list';
import {
  baseKeymap,
  wrapIn,
  setBlockType,
  chainCommands,
  toggleMark as toggleMarkOverSelection,
  exitCode,
  joinUp,
  joinDown,
  lift,
  selectParentNode
} from 'prosemirror-commands';
import { history, undo, redo } from 'prosemirror-history';
import { defaultMarkdownParser, defaultMarkdownSerializer } from 'prosemirror-markdown';
import { Transform } from 'prosemirror-transform';
import { Schema, Node, DOMSerializer } from 'prosemirror-model';
import { schema } from './schema';
import markExtents from './markExtents';
import HasChangesPlugin from './HasChangesPlugin';

const toggleClass = element => className => predicate => {
  element.classList[predicate ? 'add' : 'remove'](className);
};
icons.addImage = {
  height: 21,
  width: 24,
  path:
    'M19.5 12c-2.483 0-4.5 2.015-4.5 4.5s2.017 4.5 4.5 4.5 4.5-2.015 4.5-4.5-2.017-4.5-4.5-4.5zm2.5 5h-2v2h-1v-2h-2v-1h2v-2h1v2h2v1zm-18 0l4-5.96 2.48 1.96 2.52-4 1.853 2.964c-1.271 1.303-1.977 3.089-1.827 5.036h-9.026zm10.82 4h-14.82v-18h22v7.501c-.623-.261-1.297-.422-2-.476v-5.025h-18v14h11.502c.312.749.765 1.424 1.318 2zm-9.32-11c-.828 0-1.5-.671-1.5-1.5 0-.828.672-1.5 1.5-1.5s1.5.672 1.5 1.5c0 .829-.672 1.5-1.5 1.5z'
};

// ::- The type of field that `FieldPrompt` expects to be passed to it.
class Field {
  // :: (Object)
  // Create a field with the given options. Options support by all
  // field types are:
  //
  // **`value`**`: ?any`
  //   : The starting value for the field.
  //
  // **`label`**`: string`
  //   : The label for the field.
  //
  // **`required`**`: ?bool`
  //   : Whether the field is required.
  //
  // **`validate`**`: ?(any) → ?string`
  //   : A function to validate the given value. Should return an
  //     error message if it is not valid.
  constructor(options) {
    this.options = options;
  }

  // render:: (state: EditorState, props: Object) → dom.Node
  // Render the field to the DOM. Should be implemented by all subclasses.

  // :: (dom.Node) → any
  // Read the field's value from its DOM node.
  read(dom) {
    return dom.value;
  }

  // :: (any) → ?string
  // A field-type-specific validation function.
  validateType(_value) {}

  validate(value) {
    if (!value && this.options.required) return 'Required field';
    return this.validateType(value) || (this.options.validate && this.options.validate(value));
  }

  clean(value) {
    return this.options.clean ? this.options.clean(value) : value;
  }
}

// Helpers to create specific types of items

function canInsert(state, nodeType) {
  let $from = state.selection.$from;
  for (let d = $from.depth; d >= 0; d--) {
    let index = $from.index(d);
    if ($from.node(d).canReplaceWith(index, index, nodeType)) return true;
  }
  return false;
}

function insertImageItem(nodeType, options) {
  return new MenuItem({
    title: 'Insert image',
    label: 'Add Image',
    enable(state) {
      return canInsert(state, nodeType);
    },
    run(state, _, view) {
      const tr = view.state.tr;
      tr.setMeta('imagePicker', 'show');
      view.dispatch(tr);
    },
    ...options
  });
}

function cmdItem(cmd, options) {
  let passedOptions = {
    label: options.title,
    run: cmd
  };
  for (let prop in options) passedOptions[prop] = options[prop];
  if ((!options.enable || options.enable === true) && !options.select)
    passedOptions[options.enable ? 'enable' : 'select'] = state => cmd(state);

  return new MenuItem(passedOptions);
}

function markActive(state, type) {
  let { from, $from, to, empty } = state.selection;
  if (empty) return type.isInSet(state.storedMarks || $from.marks());
  else return state.doc.rangeHasMark(from, to, type);
}

function markItem(markType, options) {
  return cmdItem(toggleMarkOverSelection(markType), {
    active(state) {
      return markActive(state, markType);
    },
    enable: true,
    run(state, dispatch, view) {
      const extents = markExtents(state, markType);
      if (state.selection.empty && extents) {
        const transaction = state.tr;
        transaction.removeMark(extents.from, extents.to, extents.mark);
        dispatch(transaction);
      } else if (extents) {
        toggleMarkOverSelection(markType)(state, dispatch);
        return true;
      } else {
        toggleMarkOverSelection(markType, { href: '' })(view.state, view.dispatch);
      }
    },
    ...options
  });
}

const localDomains = [
  'tota.world',
  'www.tota.world',
  'tota.us-3.evennode.com',
  'tota-staging.us-3.evennode.com',
  'localhost'
];
const localDomainPreamble = new RegExp(
  `^(https?:)?(\/\/)?(${localDomains.join('|').replace('.', '\\.')}(:[0-9]+)?)\/?`,
  'i'
);
const makeKnownLocalDomainsRelative = str => `${str.replace(localDomainPreamble, '/')}`;
// TODO: Move these out into tests.
console.assert(
  makeKnownLocalDomainsRelative('http://localhost:8017/edit-article/730/') === '/edit-article/730/',
  'http://localhost:8017/edit-article/730/'
);
console.assert(
  makeKnownLocalDomainsRelative('https://localhost:8017/edit-article/730/') ===
    '/edit-article/730/',
  'https://localhost:8017/edit-article/730/'
);
console.assert(
  makeKnownLocalDomainsRelative('localhost:8017/edit-article/730/') === '/edit-article/730/',
  'localhost:8017/edit-article/730/'
);
console.assert(
  makeKnownLocalDomainsRelative('http://localhost:8017/') === '/',
  'http://localhost:8017/'
);
console.assert(
  makeKnownLocalDomainsRelative('http://localhost:8017') === '/',
  'http://localhost:8017'
);
console.assert(
  makeKnownLocalDomainsRelative('http://google.com/') === 'http://google.com/',
  'http://google.com/'
);

function linkItem(markType, options) {
  return new MenuItem({
    title: 'Add or remove link',
    icon: icons.link,
    active(state) {
      return markExtents(state, markType);
    },
    enable(state) {
      const extents = markExtents(state, markType);
      if (state.selection.empty && !extents) {
        return false;
      }
      const $start = state.doc.resolve(state.selection.from);
      const $end = state.doc.resolve(state.selection.to);
      if ($start.sameParent($end)) {
        return true;
      }
      // Selection terminals are in different nodes, but text is selected only in one node.
      if (
        ($start.nodeAfter === null &&
          $start.path[$start.path.length - 5] + 1 === $end.path[$end.path.length - 5]) ||
        ($end.nodeBefore === null &&
          $start.path[$start.path.length - 5] + 1 === $end.path[$end.path.length - 5]) ||
        ($end.nodeBefore === null &&
          $start.nodeAfter === null &&
          $start.path[$start.path.length - 5] + 2 === $end.path[$end.path.length - 5])
      ) {
        return true;
      }
      return false;
    },
    run(state, dispatch, view) {
      const extents = markExtents(state, markType);
      if (state.selection.empty && extents) {
        const transaction = state.tr;
        transaction.removeMark(extents.from, extents.to, extents.mark);
        dispatch(transaction);
      } else if (extents) {
        toggleMarkOverSelection(markType)(state, dispatch);
      } else {
        const transaction = state.tr;
        transaction.setMeta('focus-link', true);
        transaction.addMark(
          state.selection.from,
          state.selection.to,
          view.state.schema.marks.link.create({ href: '' })
        );
        dispatch(transaction);
      }
    },
    ...options
  });
}

function wrapListItem(nodeType, options) {
  return cmdItem(wrapInList(nodeType, options.attrs), options);
}

function blockTypeItem(nodeType, options) {
  let toggle = setBlockType(options.toggleType, options.toggleTypeAttrs);
  let command = setBlockType(nodeType, options.attrs);
  const active = function(state) {
    let { $from, to, node } = state.selection;
    if (node) return node.hasMarkup(nodeType, options.attrs);
    return to <= $from.end() && $from.parent.hasMarkup(nodeType, options.attrs);
  };
  let passedOptions = {
    run(state, ...rest) {
      if (active(state)) {
        return toggle(state, ...rest);
      } else {
        return command(state, ...rest);
      }
    },
    enable(state) {
      // TODO: It might make more sense for this to actually work at some point.
      return true; // command(state);
    },
    active
  };
  for (let prop in options) passedOptions[prop] = options[prop];
  return new MenuItem(passedOptions);
}

// :: (Schema) → Object
// Given a schema, look for default mark and node types in it and
// return an object with relevant menu items relating to those marks:
//
// **`toggleStrong`**`: MenuItem`
//   : A menu item to toggle the [strong mark](#schema-basic.StrongMark).
//
// **`toggleEm`**`: MenuItem`
//   : A menu item to toggle the [emphasis mark](#schema-basic.EmMark).
//
// **`toggleCode`**`: MenuItem`
//   : A menu item to toggle the [code font mark](#schema-basic.CodeMark).
//
// **`toggleLink`**`: MenuItem`
//   : A menu item to toggle the [link mark](#schema-basic.LinkMark).
//
// **`insertImage`**`: MenuItem`
//   : A menu item to insert an [image](#schema-basic.Image).
//
// **`wrapBulletList`**`: MenuItem`
//   : A menu item to wrap the selection in a [bullet list](#schema-list.BulletList).
//
// **`wrapOrderedList`**`: MenuItem`
//   : A menu item to wrap the selection in an [ordered list](#schema-list.OrderedList).
//
// **`wrapBlockQuote`**`: MenuItem`
//   : A menu item to wrap the selection in a [block quote](#schema-basic.BlockQuote).
//
// **`makeParagraph`**`: MenuItem`
//   : A menu item to set the current textblock to be a normal
//     [paragraph](#schema-basic.Paragraph).
//
// **`makeCodeBlock`**`: MenuItem`
//   : A menu item to set the current textblock to be a
//     [code block](#schema-basic.CodeBlock).
//
// **`makeHead[N]`**`: MenuItem`
//   : Where _N_ is 1 to 6. Menu items to set the current textblock to
//     be a [heading](#schema-basic.Heading) of level _N_.
//
// **`insertHorizontalRule`**`: MenuItem`
//   : A menu item to insert a horizontal rule.
//
// The return value also contains some prefabricated menu elements and
// menus, that you can use instead of composing your own menu from
// scratch:
//
// **`insertMenu`**`: Dropdown`
//   : A dropdown containing the `insertImage` and
//     `insertHorizontalRule` items.
//
// **`typeMenu`**`: Dropdown`
//   : A dropdown containing the items for making the current
//     textblock a paragraph, code block, or heading.
//
// **`fullMenu`**`: [[MenuElement]]`
//   : An array of arrays of menu elements for use as the full menu
//     for, for example the [menu bar](https://github.com/prosemirror/prosemirror-menu#user-content-menubar).
function buildMenuItems(schema) {
  let r = {},
    type;
  if ((type = schema.marks.strong))
    r.toggleStrong = markItem(type, {
      title: 'Toggle Bold',
      icon: icons.strong
    });
  if ((type = schema.marks.em))
    r.toggleEm = markItem(type, { title: 'Toggle Italics', icon: icons.em });
  if ((type = schema.marks.link))
    r.toggleLink = linkItem(type, { title: 'Toggle Link', icon: icons.link });
  if ((type = schema.nodes.image))
    r.insertImage = insertImageItem(type, { title: 'Insert an Image', icon: icons.addImage });
  if ((type = schema.nodes.bullet_list))
    r.wrapBulletList = wrapListItem(type, {
      title: 'Wrap in bullet list',
      icon: icons.bulletList
    });
  if ((type = schema.nodes.ordered_list))
    r.wrapOrderedList = wrapListItem(type, {
      title: 'Wrap in ordered list',
      icon: icons.orderedList
    });
  if ((type = schema.nodes.blockquote))
    r.wrapBlockQuote = wrapItem(type, {
      title: 'Wrap in block quote',
      icon: icons.blockquote
    });
  if ((type = schema.nodes.heading)) {
    r.makeHeading = blockTypeItem(type, {
      toggleType: schema.nodes.paragraph,
      title: 'Change to heading.',
      label: 'Heading',
      attrs: { level: 1 }
    });
    r.makeSubHeading = blockTypeItem(type, {
      toggleType: schema.nodes.paragraph,
      title: 'Change to sub-heading.',
      label: 'Sub-Heading',
      attrs: { level: 2 }
    });
  }

  // if ((type = schema.nodes.horizontal_rule)) {
  //   let hr = type;
  //   r.insertHorizontalRule = new MenuItem({
  //     title: "Insert horizontal rule",
  //     label: "Horizontal rule",
  //     enable(state) {
  //       return canInsert(state, hr);
  //     },
  //     run(state, dispatch) {
  //       dispatch(state.tr.replaceSelectionWith(hr.create()));
  //     }
  //   });
  // }

  let cut = arr => arr.filter(x => x);
  r.insertMenu = new Dropdown(cut([r.insertImage, r.insertHorizontalRule]), {
    label: 'Insert'
  });
  r.typeMenu = [cut([r.makeHeading, r.makeSubHeading])];

  r.inlineMenu = [cut([r.toggleStrong, r.toggleEm, r.toggleCode, r.toggleLink])];
  r.blockMenu = [
    cut([r.wrapBulletList, r.wrapOrderedList, r.wrapBlockQuote, joinUpItem, liftItem])
  ];
  r.fullMenu = r.inlineMenu.concat(r.typeMenu, [[undoItem, redoItem]], r.blockMenu, [
    [r.insertImage]
  ]);

  return r;
}

const mac = typeof navigator != 'undefined' ? /Mac/.test(navigator.platform) : false;

// :: (Schema, ?Object) → Object
// Inspect the given schema looking for marks and nodes from the
// basic schema, and if found, add key bindings related to them.
// This will add:
//
// * **Mod-b** for toggling [strong](#schema-basic.StrongMark)
// * **Mod-i** for toggling [emphasis](#schema-basic.EmMark)
// * **Mod-`** for toggling [code font](#schema-basic.CodeMark)
// * **Ctrl-Shift-0** for making the current textblock a paragraph
// * **Ctrl-Shift-1** to **Ctrl-Shift-Digit6** for making the current
//   textblock a heading of the corresponding level
// * **Ctrl-Shift-Backslash** to make the current textblock a code block
// * **Ctrl-Shift-8** to wrap the selection in an ordered list
// * **Ctrl-Shift-9** to wrap the selection in a bullet list
// * **Ctrl->** to wrap the selection in a block quote
// * **Enter** to split a non-empty textblock in a list item while at
//   the same time splitting the list item
// * **Mod-Enter** to insert a hard break
// * **Mod-_** to insert a horizontal rule
// * **Backspace** to undo an input rule
// * **Alt-ArrowUp** to `joinUp`
// * **Alt-ArrowDown** to `joinDown`
// * **Mod-BracketLeft** to `lift`
// * **Escape** to `selectParentNode`
//
// You can suppress or map these bindings by passing a `mapKeys`
// argument, which maps key names (say `"Mod-B"` to either `false`, to
// remove the binding, or a new key name string.
function buildKeymap(schema, mapKeys) {
  let keys = {},
    type;
  function bind(key, cmd) {
    if (mapKeys) {
      let mapped = mapKeys[key];
      if (mapped === false) return;
      if (mapped) key = mapped;
    }
    keys[key] = cmd;
  }

  bind('Mod-z', undo);
  bind('Shift-Mod-z', redo);
  bind('Backspace', undoInputRule);
  if (!mac) bind('Mod-y', redo);

  bind('Alt-ArrowUp', joinUp);
  bind('Alt-ArrowDown', joinDown);
  bind('Mod-BracketLeft', lift);
  bind('Escape', selectParentNode);

  if ((type = schema.marks.strong)) bind('Mod-b', toggleMarkOverSelection(type));
  if ((type = schema.marks.em)) bind('Mod-i', toggleMarkOverSelection(type));
  if ((type = schema.marks.code)) bind('Mod-`', toggleMarkOverSelection(type));

  if ((type = schema.nodes.bullet_list)) bind('Shift-Ctrl-8', wrapInList(type));
  if ((type = schema.nodes.ordered_list)) bind('Shift-Ctrl-9', wrapInList(type));
  if ((type = schema.nodes.blockquote)) bind('Ctrl->', wrapIn(type));
  if ((type = schema.nodes.hard_break)) {
    let br = type,
      cmd = chainCommands(exitCode, (state, dispatch) => {
        dispatch(state.tr.replaceSelectionWith(br.create()).scrollIntoView());
        return true;
      });
    bind('Mod-Enter', cmd);
    bind('Shift-Enter', cmd);
    if (mac) bind('Ctrl-Enter', cmd);
  }
  if ((type = schema.nodes.list_item)) {
    bind('Enter', splitListItem(type));
    bind('Mod-[', liftListItem(type));
    bind('Mod-]', sinkListItem(type));
  }
  if ((type = schema.nodes.paragraph)) bind('Shift-Ctrl-0', setBlockType(type));
  if ((type = schema.nodes.code_block)) bind('Shift-Ctrl-\\', setBlockType(type));
  if ((type = schema.nodes.heading))
    for (let i = 1; i <= 6; i++) bind('Shift-Ctrl-' + i, setBlockType(type, { level: i }));
  if ((type = schema.nodes.horizontal_rule)) {
    let hr = type;
    bind('Mod-_', (state, dispatch) => {
      dispatch(state.tr.replaceSelectionWith(hr.create()).scrollIntoView());
      return true;
    });
  }

  return keys;
}

// : (NodeType) → InputRule
// Given a blockquote node type, returns an input rule that turns `"> "`
// at the start of a textblock into a blockquote.
function blockQuoteRule(nodeType) {
  return wrappingInputRule(/^\s*>\s$/, nodeType);
}

// : (NodeType) → InputRule
// Given a list node type, returns an input rule that turns a number
// followed by a dot at the start of a textblock into an ordered list.
function orderedListRule(nodeType) {
  return wrappingInputRule(
    /^(\d+)\.\s$/,
    nodeType,
    match => ({ order: +match[1] }),
    (match, node) => node.childCount + node.attrs.order == +match[1]
  );
}

// : (NodeType) → InputRule
// Given a list node type, returns an input rule that turns a bullet
// (dash, plush, or asterisk) at the start of a textblock into a
// bullet list.
function bulletListRule(nodeType) {
  return wrappingInputRule(/^\s*([-+*])\s$/, nodeType);
}

// : (NodeType) → InputRule
// Given a code block node type, returns an input rule that turns a
// textblock starting with three backticks into a code block.
function codeBlockRule(nodeType) {
  return textblockTypeInputRule(/^```$/, nodeType);
}

// : (NodeType, number) → InputRule
// Given a node type and a maximum level, creates an input rule that
// turns up to that number of `#` characters followed by a space at
// the start of a textblock into a heading whose level corresponds to
// the number of `#` signs.
function headingRule(nodeType, maxLevel) {
  return textblockTypeInputRule(new RegExp('^(#{1,' + maxLevel + '})\\s$'), nodeType, match => ({
    level: match[1].length
  }));
}

// : (Schema) → Plugin
// A set of input rules for creating the basic block quotes, lists,
// code blocks, and heading.
function buildInputRules(schema) {
  let rules = smartQuotes.concat(ellipsis, emDash),
    type;
  if ((type = schema.nodes.blockquote)) rules.push(blockQuoteRule(type));
  if ((type = schema.nodes.ordered_list)) rules.push(orderedListRule(type));
  if ((type = schema.nodes.bullet_list)) rules.push(bulletListRule(type));
  if ((type = schema.nodes.code_block)) rules.push(codeBlockRule(type));
  if ((type = schema.nodes.heading)) rules.push(headingRule(type, 6));
  return inputRules({ rules });
}

export default (
  { container, utils, components, content, onChange, imageSource, getText, placeholder },
  renderCallback
) => {

  class ImageView {
    constructor(node, view, getPos) {
      this.img = document.createElement('img');
      this.img.src = /^http:\/\/|^https:\/\//.test(node.attrs.src)
        ? node.attrs.src
        : imageSource(node.attrs.src, /\//.test(node.attrs.src));
      this.img.alt = node.attrs.alt;
      this.node = node;
      this.view = view;

      this.dom = document.createElement('div').appendChild(this.img);
    }
    update(node, decorations) {
      this.node = node;
    }
    destroy() {
      this.destroyed = true;
    }
  }

  const options = { schema, mapKeys: true, menuBar: true, floatingMenu: false };

  const menus = buildMenuItems(options.schema);

  const hasChanges = HasChangesPlugin();

  class WYSIWYG {
    constructor(target, content, options) {
      const defaultEmptyState = schema.nodeFromJSON({
        type: 'doc',
        content: [{ type: 'paragraph' }]
      });
      // NOTE: We need the selection to be inside of an ephemeral, empty text node for most of the editor buttons to work.
      const defaultSelection = Selection.fromJSON(defaultEmptyState, {
        type: 'text',
        anchor: 1,
        head: 1
      });

      // Note: content is expected to be either a json object, a string of markdown, or a falsey empty string
      if (content) {
        if (typeof content === 'object') {
          content = schema.nodeFromJSON(content);
        } else {
          content = defaultMarkdownParser.parse(content);
        }
      }

      const editorState = EditorState.create({
        doc: content ? content : defaultEmptyState,
        selection: content ? undefined : defaultSelection
      }).reconfigure({
        schema: options.schema,
        plugins: [
          ImagePickerPlugin(components, imageSource),
          menuBar({
            content: menus.fullMenu
          }),
          hasChanges,
          buildInputRules(options.schema),
          keymap(buildKeymap(options.schema, options.mapKeys)),
          keymap(baseKeymap),
          dropCursor(),
          gapCursor(),
          history(),
          // NOTE: This needs to come after the menuBar for some rendering order reasons.
          LinkEditorPlugin()
        ]
      });
      const state = editorState.doc.toJSON();
      const updatedContent = state.content.reduce((content, node) => {
        // Break image nodes out of paragraphs.
        if (
          node.type === 'paragraph' &&
          node.content &&
          node.content.length &&
          node.content.some(node => node.type === 'image')
        ) {
          // Set
          return content.concat(
            node.content.map(childNode => {
              // TODO: We should force these into the role of "caption" when that is supported.
              if (childNode.type !== 'image') {
                return { type: node.type, content: [childNode] };
              }
              return { type: 'image', attrs: childNode.attrs };
            })
          );
        }
        return content.concat(node);
      }, []);
      editorState.doc = schema.nodeFromJSON({
        type: 'doc',
        content: updatedContent
      });

      this.view = new EditorView(target, {
        nodeViews: {
          image(node, view, getPos, decorations) {
            return new ImageView(node, view, getPos, decorations);
          },
          paragraph(node) {
            return new ParagraphView(node);
          }
        },
        state: editorState
      });
    }

    getMarkdown() {
      return defaultMarkdownSerializer.serialize(this.view.state.doc);
    }

    getImagesInArticle() {
      var content = this.view.state.doc.content.content;
      var imagesInArticle = {
        byId: [],
        bySrc: []
      };
      for (let i = 0; i < content.length; i++) {
        if (content[i].type.name === 'image') {
          if(content[i].attrs.id) {
            imagesInArticle.byId.push(content[i].attrs.id);
          } else {
            imagesInArticle.bySrc.push(imageSource(content[i].attrs.src));
          }
        }
      }
      return imagesInArticle;
    }

    getJSON() {
      return JSON.stringify(this.view.state.doc);
    }

    getDoc() {
      return this.view.state.doc;
    }

    getHTML() {
      return DOMSerializer.fromSchema(schema).serializeFragment(this.view.state.doc.content);
    }

    focus() {
      this.view.focus();
    }
    destroy() {
      this.view.destroy();
    }
    hasChanges() {
      return hasChanges.getState(this.view.state);
    }
    resetHasChanges() {
      const transaction = this.view.state.tr;
      transaction.setMeta('reset-has-changes', true);
      this.view.dispatch(transaction);
    }
  }

  return new WYSIWYG(container, content, options);
};
